import React from 'react';
import { isEmpty } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import ClaimFlagsViewAndUpdate from '~/components/ClaimFlags/ClaimFlagsViewAndUpdate';
import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import { usePolicy } from '~/components/PolicyContainer';

import GenericClaimBarField from './GenericClaimBarField';

const ClaimFlags = ({ displayName = 'Flags' }: { displayName?: string }) => {
  const { claim, asyncRefreshData } = useClaim({ claimRequired: true });
  const { policy } = usePolicy();
  const { isOperational } = useClaimFlagConfigurations();

  // The claim-bar entry is unconditionally on for every organization, so this is what decides
  // whether the field appears, and it appears only when there is something to put in it: a flag
  // anyone could apply, or a flag this claim already carries.
  //
  // The second half is not redundant. An administrator who disables every configuration leaves
  // isOperational false while claims still carry the flags applied when it was true — nothing
  // removes those — and this field is the only place to see or clear them. Keying on isOperational
  // alone would strand them; dropping the gate altogether would instead put a "Flags" headline over
  // blank space on every claim of an organization that has configured nothing, which is most of them.
  const hasSomethingToShow = isOperational || !isEmpty(claim.claim_flag_keys);

  return hasSomethingToShow ? (
    <GenericClaimBarField
      title={displayName}
      content={
        <ClaimFlagsViewAndUpdate
          claimId={claim.id}
          appliedFlagKeys={claim.claim_flag_keys}
          claimLob={claim.lob}
          claimSubOrganizationId={policy?.sub_organization?.id ?? null}
          onUpdate={asyncRefreshData}
        />
      }
    />
  ) : null;
};

export default ClaimFlags;
